import React from "react";
import { useMemo } from "react";
import {
  GoogleMap,
  Polyline,
  useJsApiLoader,
  InfoWindow,
  OverlayView,
  DirectionsRenderer
} from "@react-google-maps/api";

import Switch from "react-switch";

import ObjectInfo from "./components/ObjectInfo";
import CustomMapMarkerPackages from "./components/CustomMapMarkerPackages";
import CustomMapMarkerDriver from "./components/CustomMapMarkerDriver";
import CustomMapMarkerCircleIcon from "./components/CustomMapMarkerCircleIcon";
import { getIconAndColor } from "../../utils/getIconAndColor";

import styles from "./route.module.scss";
import DriverInfo from "./components/DriverInfo";

let GoogleMapsRoute = ({
  setCenter,
  packages,
  locations,
  visibleToggle,
  centralizeCoordinates,
  zoom,
  hasDetailsRoute,
  mapContext = "routeDetails",
  loadData
}) => {
  let delayFactor = 0
  const [map, setMap] = React.useState(null)
  const [initLocationRoute, setInitLocationRoute] = React.useState(null)
  const [endLocationRoute, setEndLocationRoute] = React.useState(null)
  const [packageslocations, setPackagesLocations] = React.useState(null)
  const [directionsResponses, setDirectionsResponses] = React.useState([]);
  const [infosLocation, setInfosLocation] = React.useState({
    hover: null,
    click: null
  })

  const [showPackagesInfos, setShowPackagesInfos] = React.useState(false);
  const [showDriverInfos, setShowDriverInfos] = React.useState(false);

  const [hasDriverRoute, setHasDriverRoute] = React.useState(false);
  const [hasPackagesLocations, setHasPackagesLocations] = React.useState(false);

  function handleZoomChange() {
    if (setCenter) {
      setCenter({
        lat: map?.center?.lat(),
        lng: map?.center?.lng()
      })
    }
  }

  const fetchDirections = () => {
    if (hasPackagesLocations) {
      const markers = packages.map(p => ({ lat: p.lat, lng: p.lng }))

      if (markers.length < 2) return;

      let segments = [];
      for (let i = 0; i < markers.length; i += 19) {
        segments.push(markers.slice(i, i + 20));
      }

      const directionsService = new window.google.maps.DirectionsService();

      const processSegment = (segmentIndex) => {
        if (segmentIndex >= segments.length) return;

        const segment = segments[segmentIndex];
        if (segment.length >= 2) {
          const origin = segment[0];
          const destination = segment[segment.length - 1];
          const waypoints = segment.slice(1, -1).map((marker) => ({
            location: { lat: marker.lat, lng: marker.lng },
            stopover: true,
          }));

          directionsService.route(
            {
              origin: { lat: origin.lat, lng: origin.lng },
              destination: { lat: destination.lat, lng: destination.lng },
              waypoints,
              travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
              if (status === 'OK') {
                setDirectionsResponses((current) => [...current, result]);

                if (segmentIndex < segments.length - 1) {
                  segments[segmentIndex + 1].unshift(destination);
                }

                delayFactor++
                setTimeout(() => {
                  processSegment(segmentIndex + 1);
                }, delayFactor * 750);
              } else {
                console.log(`Erro ao buscar direções para o segmento ${segmentIndex}: ${status}`);
              }
            }
          );
        }
      };
      processSegment(0);
    }
  };

  const lightThemeStyle = [
    {
      featureType: "poi",
      elementType: "labels.text",
      stylers: [
        {
          visibility: "off"
        }
      ]
    },
    {
      featureType: "poi.business",
      stylers: [
        {
          visibility: "off"
        }
      ]
    },
    {
      featureType: "road",
      elementType: "labels.icon",
      stylers: [
        {
          visibility: "off"
        }
      ]
    },
    {
      featureType: "transit",
      stylers: [
        {
          visibility: "off"
        }
      ]
    }
  ];

  const brazilCoordinates = {
    lat: -14.2400732,
    lng: -53.1805017
  };

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  });

  const iconOptions = useMemo(() => {
    return locations.map((location) => getIconAndColor(location));
  }, [locations]);

  React.useEffect(() => {
    if (hasDriverRoute) {
      setInitLocationRoute(locations[0])
      setEndLocationRoute(locations[locations.length - 1])
      setShowDriverInfos(true)
    }

    if (hasPackagesLocations && !hasDriverRoute) {
      setPackagesLocations(packages)
      setShowPackagesInfos(true)
    }
  }, [hasDriverRoute, hasPackagesLocations])

  React.useEffect(() => {
    setHasDriverRoute(!!locations?.length)
    setHasPackagesLocations(!!packages?.length && packages.length > 1)
  }, [])

  React.useEffect(() => {
    if (hasPackagesLocations) {
      fetchDirections()
    }
  }, [hasPackagesLocations])

  const showActions = (hasPackagesLocations && visibleToggle) || (hasDriverRoute && visibleToggle)
  return (
    isLoaded && (
      <div className={styles.mapContainer}>
        {showActions && <div className={styles.mapActions}>
          {hasPackagesLocations && visibleToggle && <div className={styles.option}>
            <span>Rota Projetada:</span>
            <Switch
              checked={showPackagesInfos}
              onChange={() =>
                setShowPackagesInfos(!showPackagesInfos)
              }
              offColor="#B3B3B3"
              onColor="#409E6A"
              offHandleColor="#FFF"
              onHandleColor="#FFF"
              uncheckedIcon={false}
              checkedIcon={false}
              width={45}
              height={23}
              handleDiameter={13}
            />
          </div>}
          {hasDriverRoute && visibleToggle && <div className={styles.option}>
            <span>Rota Realizada:</span>
            <Switch
              checked={showDriverInfos}
              onChange={() =>
                setShowDriverInfos(!showDriverInfos)
              }
              offColor="#B3B3B3"
              onColor="#409E6A"
              offHandleColor="#FFF"
              onHandleColor="#FFF"
              uncheckedIcon={false}
              checkedIcon={false}
              width={45}
              height={23}
              handleDiameter={13}
            />
          </div>}
        </div>}
        <div style={{ display: "flex", width: "100%", height: "600px" }}>
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "100%"
            }}
            zoom={zoom}
            options={{
              styles: lightThemeStyle
            }}
            center={
              hasDetailsRoute && (hasDriverRoute || hasPackagesLocations)
                ? hasDriverRoute
                  ? initLocationRoute
                  : packageslocations[0]
                : centralizeCoordinates
                  ? centralizeCoordinates
                  : brazilCoordinates
            }
            onZoomChanged={handleZoomChange}
            onLoad={(map) => setMap(map)}
          >
            {mapContext === "routeDetails" ? (
              <>
                {showPackagesInfos && packages.map((loc, i) => (
                  <>
                    <OverlayView
                      position={loc}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      zIndex={10}
                      key={`package-pin-${i}`}
                    >
                      <div
                        onClick={() => setInfosLocation(prev => ({ ...prev, click: i }))}
                        onMouseOver={() => setInfosLocation(prev => ({ ...prev, hover: i }))}
                        onMouseOut={() => setInfosLocation(prev => ({ ...prev, hover: null }))}
                        style={{
                          position: "relative",
                          right: "12px",
                          bottom: "25px",
                          display: "flex",
                          gap: "2px"
                        }}
                      >
                        <CustomMapMarkerPackages loc={loc} />
                      </div>
                    </OverlayView>

                    {(infosLocation.click === i || infosLocation.hover === i) && (
                      <InfoWindow
                        position={loc}
                        onCloseClick={() => setInfosLocation(prev => ({ ...prev, click: null }))}
                        options={{
                          pixelOffset: new window.google.maps.Size(0, -30),
                        }}
                      >
                        <ObjectInfo loc={loc} />
                      </InfoWindow>
                    )}
                  </>
                ))}

                {showDriverInfos && <>
                  <OverlayView
                    position={initLocationRoute}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    zIndex={10}
                    key={`driver-pin-start`}
                  >
                    <div
                      onClick={() => setInfosLocation(prev => ({ ...prev, click: "O" }))}
                      onMouseOver={() => setInfosLocation(prev => ({ ...prev, hover: "O" }))}
                      onMouseOut={() => setInfosLocation(prev => ({ ...prev, hover: null }))}
                      style={{
                        position: "relative",
                        right: "22px",
                        bottom: "54px",
                        display: "flex",
                        gap: "2px"
                      }}
                    >
                      <CustomMapMarkerDriver text={"P"} />
                    </div>
                  </OverlayView>
                  {(infosLocation.click === "O" || infosLocation.hover === "O") && (
                    <InfoWindow
                      position={initLocationRoute}
                      onCloseClick={() => setInfosLocation(prev => ({ ...prev, click: null }))}
                      options={{
                        pixelOffset: new window.google.maps.Size(5, -36),
                      }}
                    >
                      <h4>Ponto de Partida</h4>
                    </InfoWindow>
                  )}
                </>}

                {showDriverInfos && <>
                  <OverlayView
                    position={endLocationRoute}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    zIndex={10}
                    key={`driver-pin-end`}
                  >
                    <div
                      onClick={() => setInfosLocation(prev => ({ ...prev, click: "D" }))}
                      onMouseOver={() => setInfosLocation(prev => ({ ...prev, hover: "D" }))}
                      onMouseOut={() => setInfosLocation(prev => ({ ...prev, hover: null }))}
                      style={{
                        position: "relative",
                        right: "22px",
                        bottom: "54px",
                        display: "flex",
                        gap: "2px"
                      }}
                    >
                      <CustomMapMarkerDriver text={"F"} />
                    </div>
                  </OverlayView>
                  {(infosLocation.click === "D" || infosLocation.hover === "D") && (
                    <InfoWindow
                      position={endLocationRoute}
                      onCloseClick={() => setInfosLocation(prev => ({ ...prev, click: null }))}
                      options={{
                        pixelOffset: new window.google.maps.Size(5, -36),
                      }}
                    >
                      <h4>Ponto Final</h4>
                    </InfoWindow>
                  )}
                </>}

                {showDriverInfos && <Polyline
                  options={{
                    strokeColor: "#0F48FC",
                    fillColor: "#0F48FC",
                    strokeOpacity: 0.7,
                    strokeWeight: 6,
                    path: locations,
                    clickable: false,
                    draggable: false,
                    editable: false,
                    visible: true,
                    radius: 30000,
                    zIndex: 1
                  }}
                />}

                {showPackagesInfos && directionsResponses.length &&
                  directionsResponses.map(directionsResponse => (
                    <DirectionsRenderer
                      options={{
                        preserveViewport: true,
                        suppressMarkers: true,
                        polylineOptions: {
                          strokeColor: '#f5cb2b',
                          strokeOpacity: 0.7,
                          strokeWeight: 6,
                          clickable: false,
                          draggable: false,
                          editable: false,
                          icons: [
                            {
                              icon: {
                                path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                scale: 2,
                                strokeColor: '#b32020',
                                fillColor: '#b32020',
                                fillOpacity: 1,
                              },
                              offset: '0%',
                              repeat: '100px',
                            },
                          ],
                        },
                      }}
                      directions={directionsResponse}
                    />
                  ))
                }
              </>
              // ) : (
              //   locations.map((location, index) => (
              //     <OverlayView
              //       key={`custom-icon-${index}`}
              //       position={location}
              //       mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              //       zIndex={10}
              //     >
              //       <div
              //         style={{
              //           display: "flex",
              //           alignItems: "center",
              //           justifyContent: "center",
              //           position: "relative",
              //           transform: "translate(-50%, -100%)",
              //         }}
              //       >
              //         <CircleIcon icon="truck" color="black" />
              //       </div>
              //     </OverlayView>
              //   ))
            ) : (
              <>
                {locations.map((location, index) => (
                  <>
                    <OverlayView
                      key={`custom-icon-${index}`}
                      position={location}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        onClick={() => setInfosLocation(prev => ({ ...prev, click: location.code }))}
                        onMouseOver={() => setInfosLocation(prev => ({ ...prev, hover: location.code }))}
                        onMouseOut={() => setInfosLocation(prev => ({ ...prev, hover: null }))}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                          transform: "translate(-50%, -100%)",
                        }}
                      >
                        <CustomMapMarkerCircleIcon
                          icon={iconOptions[index].icon}
                          color={iconOptions[index].color}
                        />
                      </div>
                    </OverlayView>
                    {(infosLocation.click === location.code || infosLocation.hover === location.code) && (
                      <InfoWindow
                        position={location}
                        onCloseClick={() => setInfosLocation(prev => ({ ...prev, click: null }))}
                        options={{
                          pixelOffset: new window.google.maps.Size(0, -30),
                        }}
                      >
                        {location?.icon_type === "driver" ? <DriverInfo loc={location} loadData={loadData} /> : <ObjectInfo loc={location} />}
                      </InfoWindow>
                    )}
                  </>
                ))}
              </>
            )}
          </GoogleMap>
        </div>
      </div>
    )
  );
};

GoogleMapsRoute = React.memo(GoogleMapsRoute);

export { GoogleMapsRoute };
